import type { AnyCollectionRow, CollectionName } from "../../../api/src/contract/collections";
export type CollectionRow = AnyCollectionRow;
export type { CollectionName };

export type ApiCommand<T = unknown> = {
  method: string;
  path: string;
  query?: Record<string, unknown>;
  params?: Record<string, unknown>;
  body?: unknown;
  headers?: Record<string, string>;
  __result?: T;
};

export type ApiCommandInput<T = unknown> = ApiCommand<T> | (() => ApiCommand<T>);

export interface ApiExecutionContext {
  baseUrl: string;
  getToken: () => Promise<string | null> | string | null;
  fetch: typeof fetch;
}

function appendQuery(params: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined) return;
  if (value === null) {
    params.append(key, "null");
    return;
  }
  if (Array.isArray(value)) {
    if (["fields", "sort"].includes(key)) {
      params.append(key, value.join(","));
    } else {
      value.forEach((item, index) => appendQuery(params, `${key}[${index}]`, item));
    }
    return;
  }
  if (value instanceof Date) {
    params.append(key, value.toISOString());
    return;
  }
  if (typeof value === "object") {
    for (const [child, nested] of Object.entries(value as Record<string, unknown>)) {
      appendQuery(params, `${key}[${child}]`, nested);
    }
    return;
  }
  params.append(key, String(value));
}

export function queryString(query?: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) appendQuery(params, key, value);
  const value = params.toString();
  return value ? `?${value}` : "";
}

export async function executeApiCommand<T>(context: ApiExecutionContext, input: ApiCommandInput<T>): Promise<T> {
  const command = typeof input === "function" ? input() : input;
  const token = await context.getToken();
  const headers = new Headers(command.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (command.body !== undefined && !(command.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const body = command.body === undefined || command.body instanceof FormData || typeof command.body === "string"
    ? command.body as BodyInit | undefined
    : JSON.stringify(command.body);
  const effectiveQuery = command.query ?? command.params;
  const response = await context.fetch(`${context.baseUrl}${command.path}${queryString(effectiveQuery)}`, {
    method: command.method,
    headers,
    body,
  });
  const text = await response.text();
  let payload: any = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
  if (!response.ok) {
    const error = new Error(payload?.errors?.[0]?.message || payload?.error || `Request failed (${response.status})`);
    Object.assign(error, payload && typeof payload === "object" ? payload : {}, { status: response.status });
    throw error;
  }
  return (payload && typeof payload === "object" && "data" in payload ? payload.data : payload) as T;
}

export const readItems = <T = CollectionRow>(collection: CollectionName | (string & {}), query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: `/items/${collection}`, query,
});
export const readItem = <T = CollectionRow>(collection: CollectionName | (string & {}), id: string | number, query?: Record<string, unknown>): ApiCommand<T> => ({
  method: "GET", path: `/items/${collection}/${id}`, query,
});
export const createItem = <T = CollectionRow>(collection: CollectionName | (string & {}), body: unknown): ApiCommand<T> => ({
  method: "POST", path: `/items/${collection}`, body,
});
export const updateItem = <T = CollectionRow>(collection: CollectionName | (string & {}), id: string | number, body: unknown): ApiCommand<T> => ({
  method: "PATCH", path: `/items/${collection}/${id}`, body,
});
export const deleteItem = (collection: CollectionName | (string & {}), id: string | number): ApiCommand<null> => ({
  method: "DELETE", path: `/items/${collection}/${id}`,
});
export const aggregate = (collection: CollectionName | (string & {}), query?: Record<string, unknown>): ApiCommand<any[]> => ({
  method: "GET", path: `/items/${collection}`, query,
});
export const readUsers = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: "/users", query,
});
export const readUser = <T = Record<string, unknown>>(id: string, query?: Record<string, unknown>): ApiCommand<T> => ({
  method: "GET", path: `/users/${id}`, query,
});
export const updateUser = <T = Record<string, unknown>>(id: string, body: unknown): ApiCommand<T> => ({
  method: "PATCH", path: `/users/${id}`, body,
});
export const readMe = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T> => ({
  method: "GET", path: "/users/me", query,
});
export const updateMe = <T = Record<string, unknown>>(body: unknown): ApiCommand<T> => ({
  method: "PATCH", path: "/users/me", body,
});
export const readActivities = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: "/activity", query,
});
export const readRoles = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: "/roles", query,
});
export const readPolicies = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: "/policies", query,
});
export const readPermissions = <T = Record<string, unknown>>(query?: Record<string, unknown>): ApiCommand<T[]> => ({
  method: "GET", path: "/permissions", query,
});
export const readUserPermissions = <T = Record<string, Record<string, unknown>>>(): ApiCommand<T> => ({
  method: "GET", path: "/permissions/me",
});
export const createPermission = <T = Record<string, unknown>>(body: unknown): ApiCommand<T> => ({
  method: "POST", path: "/permissions", body,
});
export const deletePermission = (id: string | number): ApiCommand<null> => ({
  method: "DELETE", path: `/permissions/${id}`,
});
export const createPolicy = <T = Record<string, unknown>>(body: unknown): ApiCommand<T> => ({
  method: "POST", path: "/policies", body,
});
export const deletePolicy = (id: string): ApiCommand<null> => ({
  method: "DELETE", path: `/policies/${id}`,
});
export const createRole = <T = Record<string, unknown>>(body: unknown): ApiCommand<T> => ({
  method: "POST", path: "/roles", body,
});
export const generateTwoFactorSecret = (password: string): ApiCommand<{ secret: string; otpauth_url: string }> => ({
  method: "POST", path: "/users/me/tfa/generate", body: { password },
});
export const enableTwoFactor = (secret: string, otp: string): ApiCommand<{ enabled: boolean }> => ({
  method: "POST", path: "/users/me/tfa/enable", body: { secret, otp },
});
export const uploadFiles = (body: FormData): ApiCommand<{ id: string; filename_download: string; type: string }> => ({
  method: "POST", path: "/files", body,
});
